import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAddPaymentMethod } from '@/api/queries/payments';
import { useUIStore } from '@/store/ui';

export function SubscribeAddCardPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const add = useAddPaymentMethod();
  const pushToast = useUIStore((s) => s.pushToast);

  const [number, setNumber] = useState('');
  const [holder, setHolder] = useState('');
  const [exp, setExp] = useState('');
  const [cvv, setCvv] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const last4 = number.replace(/\s/g, '').slice(-4);
    if (!/^\d{4}$/.test(last4) || !/^\d{2}\s*\/\s*\d{2}$/.test(exp)) {
      pushToast({ body: t('errors.validation'), variant: 'error' });
      return;
    }
    const [mm, yy] = exp.split('/').map((x) => x.trim());
    try {
      await add.mutateAsync({
        brand: 'visa',
        last4,
        expiry_month: Number(mm),
        expiry_year: 2000 + Number(yy),
        cardholder_name: holder,
      });
      navigate(-1);
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
    // CVV is intentionally not posted — backend stores stub-only data.
    void cvv;
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('subscribe.card_form.title')} />
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label={t('subscribe.card_form.number')}
            inputMode="numeric"
            placeholder="•••• •••• •••• ••••"
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/[^\d\s]/g, ''))}
            maxLength={19}
          />
          <Input
            label={t('subscribe.card_form.cardholder')}
            value={holder}
            onChange={(e) => setHolder(e.target.value)}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input
              label={t('subscribe.card_form.expiry')}
              inputMode="numeric"
              placeholder="MM / YY"
              value={exp}
              onChange={(e) => setExp(e.target.value)}
            />
            <Input
              label={t('subscribe.card_form.cvv')}
              inputMode="numeric"
              type="password"
              maxLength={4}
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <Button type="submit" block size="lg" variant="primary" loading={add.isPending}>
            {t('subscribe.card_form.save')}
          </Button>
        </form>
      </PageContainer>
    </AppShell>
  );
}
